import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useRegionalExperience } from "../../../hooks/useRegionalExperience";

const DEFAULT_CAMPAIGN_IMAGE = "/assets/agm/polyclinic.jpg";

function getSafeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
}

// Progress bar / "% funded" UI only renders once a campaign crosses this
// threshold. Below it (e.g. brand-new "0% funded, 1 raised of 5,000,000"
// campaigns) we fall back to the open-campaign donor line below.
const MIN_PROGRESS_PERCENT = 5;

const FundraisingCard = ({
  title,
  backgroundImage,
  description,
  additionalText,
  bulletPoints,
  achieved,
  goal,
  donors,
  id,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { regionKey } = useRegionalExperience();
  // USA donors see western grouping (5,000,000); India keeps lakh/crore (50,00,000).
  const numberLocale = regionKey === "US" ? "en-US" : "en-IN";
  const formatNumber = (value) =>
    new Intl.NumberFormat(numberLocale).format(getSafeNumber(value));
  const safeAchieved = getSafeNumber(achieved);
  const safeGoal = getSafeNumber(goal);
  const safeDonors = getSafeNumber(donors);
  const percentage = safeGoal ? Math.min(Math.round((safeAchieved / safeGoal) * 100), 100) : 0;
  const hasMeaningfulProgress = safeGoal > 0 && percentage >= MIN_PROGRESS_PERCENT;
  const imageSrc = backgroundImage || DEFAULT_CAMPAIGN_IMAGE;

  const handleDonateClick = () => {
    const params = new URLSearchParams({ region: regionKey });
    if (id) params.set("campaignId", id);
    navigate(`/donate-form?${params.toString()}`);
  };

  return (
    <article className="group overflow-hidden rounded-lg border border-[#dfe8e6] bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-xl">
      <div className="aspect-[16/10] overflow-hidden bg-[#edf4f2]">
        <img
          src={imageSrc}
          alt={title}
          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
          loading="lazy"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = DEFAULT_CAMPAIGN_IMAGE;
          }}
        />
      </div>

      <div className="p-6">
        <h3 className="text-2xl font-semibold leading-tight text-[#14201f]">{title}</h3>
        <p className="mt-4 text-sm leading-7 text-[#52615f]">{description}</p>
        {additionalText && <p className="mt-3 text-sm leading-7 text-[#52615f]">{additionalText}</p>}

        {bulletPoints?.length > 0 && (
          <ul className="mt-5 space-y-3">
            {bulletPoints.slice(0, 4).map((point, index) => (
              <li key={`${point}-${index}`} className="flex gap-3 text-sm leading-6 text-[#33413f]">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#9d1f1c]" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        )}

        {!(safeGoal === 0 && safeAchieved === 0 && safeDonors === 0) && (
          hasMeaningfulProgress ? (
            <div className="mt-6">
              <div className="h-2 overflow-hidden rounded-full bg-[#e7efed]">
                <div className="h-full rounded-full bg-[#9d1f1c]" style={{ width: `${percentage}%` }} />
              </div>
              <div className="mt-3 flex items-center justify-between text-xs font-semibold uppercase tracking-[0.14em] text-[#52615f]">
                <span>{t("donate.card.percentFunded", { percentage })}</span>
                <span>{formatNumber(safeDonors)} {t("donate.card.donors")}</span>
              </div>
              <p className="mt-2 text-sm font-medium text-[#33413f]">
                {formatNumber(safeAchieved)} {t("donate.card.raisedOf")} {formatNumber(safeGoal)} {t("donate.card.goal")}
              </p>
            </div>
          ) : (
            <div className="mt-6 rounded-md bg-[#f1f6f4] px-4 py-3 text-sm font-medium text-[#52615f]">
              {safeDonors > 0
                ? t("donate.card.donorsSupporting", { count: formatNumber(safeDonors) })
                : t("donate.card.beFirstDonor")}
            </div>
          )
        )}

        <button
          type="button"
          className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-md bg-[#9d1f1c] px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-white transition hover:bg-[#7f1916]"
          onClick={handleDonateClick}
        >
          {t("donate.card.donateToCause")}
          <ArrowRight size={18} />
        </button>
      </div>
    </article>
  );
};

FundraisingCard.propTypes = {
  title: PropTypes.string.isRequired,
  backgroundImage: PropTypes.string,
  achieved: PropTypes.number,
  goal: PropTypes.number,
  donors: PropTypes.number,
  description: PropTypes.string.isRequired,
  additionalText: PropTypes.string,
  bulletPoints: PropTypes.arrayOf(PropTypes.string),
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default FundraisingCard;
